"use client";

import { THANK_YOU_SOURCE } from "@/constants/leadConstants";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useState } from "react";

interface ThankYouData {
  id?: number;
  page_id?: number;
  component_id?: number;
  variant?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  gradient_angle?: string;
  gradient_start_color?: string;
  gradient_start_position?: string;
  gradient_end_color?: string;
  gradient_end_position?: string;
  mobile_gradient_angle?: string;
  mobile_gradient_start_color?: string;
  mobile_gradient_start_position?: string;
  mobile_gradient_end_color?: string;
  mobile_gradient_end_position?: string;
  description?: string;
  referral_description?: string;
  influencer_description?: string;
  status?: number;
}

interface ThankYou1Props {
  data?: {
    data?: ThankYouData;
  };
}

export default function ThankYou1({ data }: ThankYou1Props) {
  const { data: thankYouData } = data || {};
  const [description, setDescription] = useState("");
  const [source, setSource] = useState<string | null>(null);
  const isMobile = useIsMobile(640);

  // Read source from sessionStorage on mount (only once)
  // Don't remove here - the form pages handle clearing the other key
  useEffect(() => {
    const storedSource = sessionStorage.getItem(THANK_YOU_SOURCE.STORAGE_KEY);
    if (storedSource) {
      setSource(storedSource);
    }
  }, []);

  // Set description when data is available and source is known
  useEffect(() => {
    if (!thankYouData) return;

    let selectedDescription = "";
    if (
      source === THANK_YOU_SOURCE.REFERRAL &&
      thankYouData?.referral_description
    ) {
      selectedDescription = thankYouData.referral_description;
    } else if (
      source === THANK_YOU_SOURCE.INFLUENCER &&
      thankYouData?.influencer_description
    ) {
      selectedDescription = thankYouData.influencer_description;
    } else {
      // Fallback to default description
      selectedDescription = thankYouData?.description || "";
    }

    setDescription(selectedDescription);
  }, [thankYouData, source]);

  const {
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "Thank You Background",
    gradient_angle = "254deg",
    gradient_start_color = "rgba(0, 0, 0, 0.3)",
    gradient_start_position = "0%",
    gradient_end_color = "rgba(0, 0, 0, 0.7)",
    gradient_end_position = "100%",
    mobile_gradient_angle = "0deg",
    mobile_gradient_start_color = "rgba(0, 0, 0, 0.8)",
    mobile_gradient_start_position = "0%",
    mobile_gradient_end_color = "rgba(0, 0, 0, 0.8)",
    mobile_gradient_end_position = "100%",
  } = thankYouData || {};

  // Build gradient styles for desktop and mobile
  const desktopGradient = `linear-gradient(${gradient_angle}, ${gradient_start_color} ${gradient_start_position}, ${gradient_end_color} ${gradient_end_position})`;
  const mobileGradient = `linear-gradient(${mobile_gradient_angle}, ${mobile_gradient_start_color} ${mobile_gradient_start_position}, ${mobile_gradient_end_color} ${mobile_gradient_end_position})`;

  return (
    <div className="w-full mx-auto px-0 sm:px-8 sm:pb-12">
      <div className="relative rounded-none sm:rounded-3xl overflow-hidden shadow-2xl">
               <div className="relative w-full h-[400px] sm:-[440px] lg:h-[502px] xl:h-[632px]">
          {desktop_bg_image && (
            <Image
              src={desktop_bg_image}
              alt={alt_image}
              fill
              className="hidden sm:block object-cover"
              priority
            />
          )}
          {mobile_bg_image && (
            <Image
              src={mobile_bg_image}
              alt={alt_image}
              fill
              className="block sm:hidden object-cover"
              priority
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: isMobile ? mobileGradient : desktopGradient,
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center sm:justify-start px-5 sm:px-10 md:px-12 xl:px-16 py-20 sm:py-17 md:py-25 xl:py-38">
            <div className="max-w-4xl text-center sm:text-left">
              {description && (
                <div
                 className={cn(
              "prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-primary-main prose-strong:text-gray-900"
            )}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
